import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ products: [] });
  const [wishlist, setWishlist] = useState({ products: [] });
  const { user } = useAuth();

  const fetchCart = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('cart');
      setCart(res.data.cart || { products: [] });
    } catch {}
  }, [user]);

  const fetchWishlist = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('wishlist');
      setWishlist(res.data.wishlist || { products: [] });
    } catch {}
  }, [user]);

  useEffect(() => {
    if (user) { fetchCart(); fetchWishlist(); }
    else { setCart({ products: [] }); setWishlist({ products: [] }); }
  }, [user, fetchCart, fetchWishlist]);

  const addToCart = async (productId, quantity = 1, size, color, isCustom, design, customThumbnail, designFile, comments, preferredSeller) => {
    const res = await api.post('cart/add', { productId, quantity, size, color, isCustom, design, customThumbnail, designFile, comments, preferredSeller });
    setCart(res.data.cart);
    return res.data;
  };

  const updateCartItem = async (itemId, quantity) => {
    const res = await api.put(`cart/item/${itemId}`, { quantity });
    setCart(res.data.cart);
  };

  const removeFromCart = async (itemId) => {
    const res = await api.delete(`cart/item/${itemId}`);
    setCart(res.data.cart);
  };

  const clearCart = async () => {
    await api.delete('cart/clear');
    setCart({ products: [] });
  };

  const toggleWishlist = async (productId) => {
    const res = await api.post('wishlist/toggle', { productId });
    setWishlist(res.data.wishlist);
    return res.data.action;
  };

  const isInWishlist = (productId) =>
    wishlist.products?.some(p => (p._id || p) === productId);

  const cartCount = cart.products?.reduce((sum, i) => sum + i.quantity, 0) || 0;
  const wishlistCount = wishlist.products?.length || 0;

  const cartTotal = cart.products?.reduce((sum, item) => {
    const price = item.product?.finalPrice || item.product?.price || 0;
    return sum + price * item.quantity;
  }, 0) || 0;

  return (
    <CartContext.Provider value={{ cart, wishlist, addToCart, updateCartItem, removeFromCart, clearCart, toggleWishlist, isInWishlist, cartCount, wishlistCount, cartTotal, fetchCart, fetchWishlist }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
